import {
  Button,
  ClickAwayListener,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/SaveOutlined';
import Box from '@material-ui/core/Box';
import cx from 'classnames';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import InputFieldWithAction from '~/src/components/InputFieldWithAction';
import useToggle from '~/src/hooks/useToggle';
import { displayName, projectType, projectVersionState } from '../store';
import Header from './Header';
import useProjectUpdate from '~/src/Projects/hooks/useProjectUpdate';
import useProjectId from '~/src/hooks/useProjectId';
import { projectListState, ProjectMenuActions } from '~/src/Projects/store';
import {
  selectedMenuActionState,
  selectedProjectIdState,
  selectedProjectNameState,
  isReadySelectedProjectState,
} from '~/src/Projects/store/dialogs.store';
import ProjectCardLastRunDate from '~/src/Projects/components/ProjectCardLastRunDate';
import ProjectTags from '~/src/Tags/components/ProjectTags';
import { Tag } from '~/src/Tags/store';

const EMPTY_TAGS: Tag[] = [];

const useStyles = makeStyles(({ spacing, typography, palette }) => ({
  name: {
    cursor: 'text',
    '&:hover': {
      background: 'rgba(0, 0, 0, .035)',
    },
    maxWidth: '100%', // Ensure it doesn't overflow its container
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  editNameInput: {
    padding: spacing(0),
    borderWidth: '0 0 1px',
    borderRadius: 0,

    '& input': {
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: '1.235rem',
      fontSize: '2.125rem',
      fontWeight: typography.fontWeightRegular,
    },
  },
  type: {
    marginLeft: spacing(2),
    color: palette.text.secondary,
    alignSelf: 'flex-end',
  },

  lastRunLabel: {
    fontSize: '1.0rem',
    color: '#888',
  },

  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: spacing(1),
  },

  tagsEditIcon: {
    color: palette.text.secondary,
    padding: 0,
    marginLeft: spacing(0.5),
  },

  tagsAddButton: {
    color: palette.text.secondary,
    borderColor: palette.divider,
    textTransform: 'none',
    fontSize: '10px',
  },
}));

const ProjectHeader = ({ handleClose, headerClassStyles = '' }) => {
  const classes = useStyles();
  const projectId = useProjectId();
  const [updateProject] = useProjectUpdate();
  const { t } = useTranslation(['projects', 'projectTypes']);
  const [editNameModeEnabled, , disableEditNameMode, toggleEditNameMode] = useToggle(false);

  // Recoil State
  const [name, setName] = useRecoilState(displayName);
  const type = useRecoilValue(projectType);
  const resultsData = useRecoilValue(projectListState);
  const version = useRecoilValue(projectVersionState);
  const setSelectedProjectId = useSetRecoilState(selectedProjectIdState);
  const setSelectedProjectName = useSetRecoilState(selectedProjectNameState);
  const setIsReadySelectedProject = useSetRecoilState(isReadySelectedProjectState);
  const setSelectedMenuAction = useSetRecoilState(selectedMenuActionState);

  // Variable
  const reports = useMemo(
    () => resultsData?.entries?.find((proj) => proj.project?._id === projectId)
      ?.project?.reports,
    [resultsData, projectId],
  );

  const tags = useMemo(
    () => resultsData?.entries?.find((proj) => proj.project?._id === projectId)
      ?.project?.tags || EMPTY_TAGS,
    [resultsData, projectId],
  );

  // Handlers
  const handleEditTags = useCallback(() => {
    setSelectedProjectId(projectId);
    setSelectedProjectName(name);
    setIsReadySelectedProject(true);
    setSelectedMenuAction(ProjectMenuActions.EditTags);
  }, [projectId, name]);

  // Handlers
  const handleChangeProjectName = useCallback(
    async (newName) => {
      try {
        await updateProject(
          projectId,
          { displayName: newName },
          t('updateProject.errors.changeName'),
        );
        setName(newName);
        toggleEditNameMode();
      } catch (error) {
        // intentionally empty
      }
    },
    [projectId, setName, t, toggleEditNameMode, updateProject],
  );

  return (
    <Header onClose={handleClose} rootClass={headerClassStyles}>
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center">
          {!editNameModeEnabled && (
            <Tooltip
              title={t('updateProject.name.clickToEdit')}
              arrow
              enterDelay={500}
              enterNextDelay={500}
            >
              <Typography
                variant="h4"
                onClick={toggleEditNameMode}
                className={classes.name}
              >
                {name}
              </Typography>
            </Tooltip>
          )}
          {editNameModeEnabled && (
            <ClickAwayListener onClickAway={disableEditNameMode}>
              <InputFieldWithAction
                autoFocus
                className={cx(
                  'seer-project-prevent-default',
                  classes.editNameInput,
                )}
                disableActionWithoutChanges
                value={name}
                actionIcon={SaveIcon}
                disableActionOnEmpty
                actionTooltip={t('projects:updateProject.name.saveButtonTooltip')}
                onActionClick={handleChangeProjectName}
              />
            </ClickAwayListener>
          )}
          <Typography
            variant="subtitle1"
            className={classes.type}
            title={`project ID: ${projectId}`}
          >
            {t(`projectTypes:${type}.name`)}
          </Typography>
          {version && (
            <Typography variant="subtitle1" className={classes.type}>
              ({version})
            </Typography>
          )}
          {reports && (
            <Typography variant="subtitle1" className={classes.type}>
              <ProjectCardLastRunDate
                reports={reports}
                className={classes.lastRunLabel}
              />
            </Typography>
          )}
        </Box>
        {tags.length > 0 && (
          <Box className={classes.tagsRow}>
            <ProjectTags tags={tags} />
            <IconButton
              size="small"
              onClick={handleEditTags}
              classes={{ root: classes.tagsEditIcon }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {tags.length === 0 && (
          <Box className={classes.tagsRow}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleEditTags}
              classes={{ root: classes.tagsAddButton }}
            >
              {t('filterProjects.addTag')}
            </Button>
          </Box>
        )}
      </Box>
    </Header>
  );
};

export default memo(ProjectHeader);
